import React, { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Headphones, Mic, BookOpen, PenTool, Printer, Download, 
  CheckCircle, FileText, ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeacherKey = () => {
  const { gradeLevel: paramGrade } = useParams();
  const [searchParams] = useSearchParams();
  const isPrintMode = searchParams.get('print') === 'true';
  const studentName = searchParams.get('student') || '';
  
  const [selectedGrade, setSelectedGrade] = useState(paramGrade || "K");
  const [grades, setGrades] = useState([]);
  const [teacherKey, setTeacherKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("listening");

  const modalities = [
    { key: "listening", label: "Listening", icon: Headphones, color: "bg-purple-100 text-purple-700" },
    { key: "speaking", label: "Speaking", icon: Mic, color: "bg-blue-100 text-blue-700" },
    { key: "reading", label: "Reading", icon: BookOpen, color: "bg-green-100 text-green-700" },
    { key: "writing", label: "Writing", icon: PenTool, color: "bg-orange-100 text-orange-700" }
  ];

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const response = await axios.get(`${API}/grades`);
        setGrades(response.data.grades);
      } catch (error) {
        console.error("Error fetching grades:", error);
      }
    };
    fetchGrades();
  }, []);

  useEffect(() => {
    const fetchTeacherKey = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/teacher-key/${selectedGrade}`);
        setTeacherKey(response.data);
      } catch (error) {
        console.error("Error fetching teacher key:", error);
        toast.error("Failed to load teacher key");
      } finally {
        setLoading(false);
      }
    };
    fetchTeacherKey();
  }, [selectedGrade]);

  const handlePrint = () => {
    window.print();
  };

  const gradeLabels = {
    "K": "Kindergarten",
    "1": "Grade 1",
    "2": "Grade 2",
    "3-4": "Grades 3-4",
    "5-6": "Grades 5-6",
    "7-8": "Grades 7-8",
    "9-12": "Grades 9-12"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="teacher-key" data-testid="teacher-key-page">
      {/* Print Header (only shows in print) */}
      <div className="hidden print:block mb-8">
        <div className="text-center border-b-2 border-black pb-4">
          <h1 className="text-2xl font-bold">MOSAIC — Mosaic Language Proficiency Assessment</h1>
          <h2 className="text-xl">{gradeLabels[selectedGrade]} - {isPrintMode && studentName ? `Student: ${studentName}` : 'TEACHER ANSWER KEY'}</h2>
          <p className="text-sm text-gray-600 mt-2">Date: {new Date().toLocaleDateString()}</p>
        </div>
      </div>

      {/* Screen Header */}
      <div className="no-print">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-[#002FA7]">Home</Link>
            <span>/</span>
            <span className="text-gray-900">Teacher Key</span>
          </div>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Teacher Answer Key
              </h1>
              <p className="text-gray-600">
                Complete exam with correct answers and scoring rubrics
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                <SelectTrigger className="w-48 h-12 border-2 border-gray-300 rounded-none" data-testid="grade-select">
                  <SelectValue placeholder="Select Grade" />
                </SelectTrigger>
                <SelectContent>
                  {grades.map(grade => (
                    <SelectItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button onClick={handlePrint} className="btn-sharp-primary h-12 px-6" data-testid="print-btn">
                <Printer size={18} className="mr-2" />
                Print
              </Button>
            </div>
          </div>

          {/* Proficiency Levels Reference — WIDA ELD level names + MOSAIC practice-score ranges */}
          <div className="border-2 border-gray-200 p-6 mb-8">
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Proficiency Level Reference</h3>
            <p className="text-xs text-gray-500 mb-4">Proficiency level descriptors (Entering–Reaching) mapped to MOSAIC practice-assessment score ranges.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {teacherKey?.wida_levels?.map(level => (
                <div key={level.level} className={`wida-badge level-${level.level} text-sm`}>
                  <span className="font-bold">{level.level}</span>
                  <span>{level.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="no-print w-full justify-start border-b border-gray-200 bg-transparent p-0 mb-8">
            {modalities.map(modality => {
              const questions = teacherKey?.modalities?.[modality.key] || [];
              return (
                <TabsTrigger
                  key={modality.key}
                  value={modality.key}
                  className="modality-tab data-[state=active]:active"
                  data-testid={`tab-${modality.key}`}
                >
                  <modality.icon size={18} className="mr-2" />
                  {modality.label}
                  <span className="ml-2 text-xs text-gray-400">({questions.length})</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {modalities.map(modality => {
            const questions = teacherKey?.modalities?.[modality.key] || [];
            const ModalityIcon = modality.icon;
            
            return (
              <TabsContent key={modality.key} value={modality.key} className="mt-0">
                {/* Section Header */}
                <div className="border-2 border-black p-6 mb-6 print:border print:mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${modality.color} flex items-center justify-center`}>
                      <ModalityIcon size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {modality.label}
                      </h2>
                      <p className="text-gray-600">
                        {questions.length} Questions • 
                        {modality.key === "listening" && " Multiple Choice"}
                        {modality.key === "reading" && " Multiple Choice"}
                        {modality.key === "speaking" && " Speaking Prompts (Rubric Scored)"}
                        {modality.key === "writing" && " Constructed Response (Rubric Scored)"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-4 print:space-y-2">
                  {questions.map((question, index) => (
                    <div 
                      key={question.id} 
                      className="border-2 border-gray-200 print:border print:page-break-inside-avoid"
                      data-testid={`question-${question.id}`}
                    >
                      {/* Question Header */}
                      <div className="flex items-start gap-4 p-6 print:p-4">
                        <div className="answer-key-number flex-shrink-0">
                          {question.question_number}
                        </div>
                        <div className="flex-1">
                          {/* Format-Aware Context Display */}
                          {question.big_picture_context && (
                            <div className="bg-blue-50 border border-blue-200 p-4 mb-4 text-sm print:p-2">
                              <span className="font-semibold text-blue-700">Big Picture: </span>
                              {question.big_picture_context}
                            </div>
                          )}
                          {question.passage_title && (
                            <div className="bg-emerald-50 border border-emerald-200 p-4 mb-4 text-sm print:p-2">
                              <span className="font-semibold text-emerald-700">Passage: {question.passage_title}</span>
                            </div>
                          )}
                          {question.task_name && (
                            <div className="bg-sky-50 border border-sky-200 p-4 mb-4 text-sm print:p-2 flex items-center gap-2">
                              <span className="font-semibold text-sky-700">Task: {question.task_name}</span>
                              {question.scaffold_level && (
                                <span className={`text-xs px-2 py-0.5 font-semibold ${question.scaffold_level === 1 ? 'bg-sky-200 text-sky-800' : 'bg-gray-200 text-gray-600'}`}>
                                  {question.scaffold_level === 1 ? 'Main' : `Support Q${question.scaffold_level}`}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Generic Stimulus Fallback */}
                          {question.stimulus && !question.big_picture_context && !question.passage_title && !question.task_name && (
                            <div className="bg-gray-50 border border-gray-200 p-4 mb-4 text-sm print:p-2">
                              <span className="font-semibold">Stimulus: </span>
                              {question.stimulus}
                            </div>
                          )}
                          
                          {/* Question Text */}
                          <p className="font-semibold mb-4">{question.question_text}</p>
                          
                          {/* Multiple Choice Options */}
                          {question.question_type === "multiple_choice" && question.options && (
                            <div className="space-y-2 mb-4">
                              {question.options.map((option, optIndex) => (
                                <div 
                                  key={optIndex}
                                  className={`p-3 border ${option === question.correct_answer 
                                    ? 'correct-answer border-2' 
                                    : 'border-gray-200'}`}
                                >
                                  <span className="font-semibold mr-2">
                                    {String.fromCharCode(65 + optIndex)}.
                                  </span>
                                  {option}
                                  {option === question.correct_answer && (
                                    <CheckCircle className="inline-block ml-2 text-green-600" size={16} />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Correct Answer for MC */}
                          {question.question_type === "multiple_choice" && (
                            <div className="answer-key-correct inline-flex">
                              <CheckCircle size={16} />
                              <span>Correct: {question.correct_answer}</span>
                            </div>
                          )}
                          
                          {/* Rubric for Speaking/Writing */}
                          {(question.question_type === "speaking_prompt" || 
                            question.question_type === "constructed_response") && question.rubric && (
                            <div className="rubric-box">
                              <div className="font-bold mb-2 text-yellow-800">
                                SCORING RUBRIC (Max Points: {question.max_points})
                              </div>
                              <pre className="whitespace-pre-wrap text-xs">{question.rubric}</pre>
                            </div>
                          )}
                          {/* Writing Scaffolds */}
                          {question.scaffold_q2 && (
                            <div className="bg-amber-50 border border-amber-200 p-3 mt-3 text-sm">
                              <div className="font-semibold text-amber-700 text-xs mb-1">SCAFFOLD QUESTIONS:</div>
                              <p className="text-amber-800">Q2: {question.scaffold_q2}</p>
                              {question.scaffold_q3 && <p className="text-amber-800">Q3: {question.scaffold_q3}</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Print Page Break */}
                <div className="hidden print:block page-break-after-always"></div>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Print Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-500">
          <p>MOSAIC Assessment • {gradeLabels[selectedGrade]} • Teacher Answer Key</p>
          <p className="text-xs mt-1">&copy; {new Date().getFullYear()} MOSAIC • Created by Lerno Co.</p>
        </div>
      </div>
    </div>
  );
};

export default TeacherKey;
